import { type FC, useMemo } from "react";
import { Link } from "@stubhub/broadway-web";
import { HeadingLevel, Layout } from "@viagogo/core/ui";
import { Content } from "src/shared/components/content";
import { useAppContext } from "src/shared/context/AppContext";
import { useScrollToggle } from "src/shared/hooks/useScrollToggle";
import { sendAnalyticsEvent } from "src/shared/utils/analyticsevent/analytics-event";
import { formatTimestampForAnalytics } from "src/shared/utils/analyticsevent/utils";
import { useFindPageModelData } from "src/website-apps/responsive/stubhub-find/hooks/useFindPageModelData";
import { AppInstallBanner, AppInstallBannerPlacements } from "src/website-apps/shared/components/app-install-banner";
import { withDefaultContexts } from "src/website-apps/shared/components/explore/withDefaultContexts";
import { MegaSearchProvider } from "src/website-apps/shared/components/instant-search-redesign-mega-search/MegaSearchProvider";
import { EventsGridContext } from "src/website-apps/shared/context/events-grid-context";
import { SearchTermProvider } from "src/website-apps/shared/context/SearchTermContext";
import { ExploreModeCards } from "./ExploreModeCards/ExploreModeCards";
import {
  backgroundColor,
  bodyWrapper,
  emailSignupWrapper,
  linkWrapper,
  searchLink,
} from "./ExploreModeSearchApp.styled";
import { FindEmailSignUp } from "./FindEmailSignUp";
import { FindLayoutFooter } from "./FindLayoutFooter";
import { FindLayoutHeader } from "./FindLayoutHeader";

const UnwrappedExploreModeSearchApp: FC = () => {
  const model = useFindPageModelData();
  const appContext = useAppContext();
  const { env } = appContext;
  const [mobileMenuOpen, setMobileMenuOpen] = useScrollToggle();

  const { privacyPolicyMessage, exploreCategories } = model;

  const searchKeyword = JSON.parse(`"${model.searchKeyword}"`) as string;
  const searchGuid = model.searchGuid ? (JSON.parse(`"${model.searchGuid}"`) as string) : null;

  const searchUrl = useMemo(() => {
    const url = new URL(window.location.href);
    url.searchParams.set("allowExplore", "false");
    return url.href;
  }, []);

  const handleSearchLinkClick = () => {
    const analyticsData = [
      {
        anonymousId: appContext.anonymousId,
        pageVisitId: appContext.pageVisitId,
        searchGuid,
        queryId: exploreCategories?.[0]?.queryId ?? null,
        term: searchKeyword,
        timestamp: formatTimestampForAnalytics(),
        applicationName: model.appName,
      },
    ];
    sendAnalyticsEvent(appContext, env, "UserRequestLexicalSearch", analyticsData);
  };

  // Minimal grid context for EventGridItem to work
  // Type assertion is safe here as we provide a minimal stub that satisfies the interface
  const minimalGridContext = useMemo(
    () =>
      [
        {
          grids: [],
          filters: {
            location: null,
            dates: null,
            gridFilterType: null,
          },
          queryParams: {},
        },
        () => {}, // noop dispatch
        {}, // empty actions
      ] as const,
    [],
  );

  return (
    <div className={backgroundColor}>
      <SearchTermProvider searchTerm={searchKeyword}>
        <MegaSearchProvider>
          <EventsGridContext.Provider value={minimalGridContext as any}>
            <HeadingLevel>
              <Layout
                header={
                  <FindLayoutHeader
                    mobileMenuOpen={mobileMenuOpen}
                    model={model}
                    setMobileMenuOpen={setMobileMenuOpen}
                  />
                }
                body={
                  <div className={bodyWrapper}>
                    <ExploreModeCards categories={exploreCategories ?? []} />

                    <div className={linkWrapper}>
                      <Content id="cid$#4091259#" />{" "}
                      <span className={searchLink}>
                        <Link href={searchUrl} onClick={handleSearchLinkClick}>
                          {searchKeyword}
                        </Link>
                      </span>
                    </div>
                    <div className={emailSignupWrapper}>
                      <FindEmailSignUp privacyPolicyMessage={privacyPolicyMessage} />
                    </div>
                    <AppInstallBanner placement={AppInstallBannerPlacements.SEARCH} userId={appContext.userId} />
                  </div>
                }
                footer={<FindLayoutFooter model={model} />}
              />
            </HeadingLevel>
          </EventsGridContext.Provider>
        </MegaSearchProvider>
      </SearchTermProvider>
    </div>
  );
};

export const ExploreModeSearchApp = withDefaultContexts(UnwrappedExploreModeSearchApp);
